import React, { useContext, useState } from 'react';
import { DialogContent, DialogContentText, useTheme } from '@mui/material';
import IconButtonWhiteBg from '../../../common/IconButtonWhiteBg';
import { AppContext } from '../../../../App';
import { useQueryClient } from '@tanstack/react-query';
import UndoIcon from '@mui/icons-material/Undo';
import { useCreateCorrection } from '../../../hooks/useCreateCorrection';
import {
  bulkCorrectionRevertDataAtom,
  bulkCorrectionSummaryAtom,
} from '../../../../atoms/spreadsheet-atoms';
import { useAtom } from 'jotai';
import DialogGlynt from '../../../common/Dialog/DialogGlynt';
import DialogTitleGlynt from '../../../common/Dialog/DialogTitleGlynt';
import DialogActionsGlynt from '../../../common/Dialog/DialogActionsGlynt';
import DialogContentGlynt from '../../../common/Dialog/DialogContentGlynt';
import { IBulkCorrectionSummary } from '../../../spreadsheet/SpreadsheetTypes';

const IconRevertBulkCorrections: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [bulkCorrectionRevertData, setBulkCorrectionRevertData] = useAtom(
    bulkCorrectionRevertDataAtom
  );
  const [, setBulkCorrectionSummary] = useAtom(bulkCorrectionSummaryAtom);
  const { setToast } = useContext(AppContext);

  const theme = useTheme();
  const queryClient = useQueryClient();

  const createCorrectionMutation = useCreateCorrection(
    true,
    false,
    {
      onSuccess: () => {
        setToast({
          variant: 'success',
          open: true,
          message: 'Corrections updated successfully',
        });
      },
      onError: (error) => {
        setToast({
          variant: 'error',
          open: true,
          message: 'Failed to update corrections',
        });

        queryClient.invalidateQueries({
          queryKey: ['extraction']
        });
      },
    },
    false
  );

  const clearBulkCorrectionsRevertData = () => {
    setBulkCorrectionRevertData([]);
    setOpen(false);
  };

  const handleCloseConfirmation = () => {
    setOpen(false);
  };

  if (bulkCorrectionRevertData?.length === 0) {
    return;
  }

  const handleUndo = async () => {
    try {
      // Reset summary at start of new operation
      setBulkCorrectionSummary([]);
      const correctionsSummary: IBulkCorrectionSummary[] = [];

      for (let i = 0; i < bulkCorrectionRevertData.length; i++) {
        const revertItem = bulkCorrectionRevertData[i];
        try {
          await createCorrectionMutation.mutateAsync({
            extraction: revertItem.extractionId,
            field: revertItem.field,
            correction: {
              content: revertItem.originalContent,
              tokens: revertItem.originalTokens,
            },
          });

          queryClient.invalidateQueries({
            queryKey: ['extraction', revertItem.extractionId]
          });

          correctionsSummary.push({
            documentIndex: i,
            documentId: revertItem.documentId,
            extractionId: revertItem.extractionId,
            status: 'success',
          });
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
          correctionsSummary.push({
            documentIndex: i,
            documentId: revertItem.documentId,
            extractionId: revertItem.extractionId,
            status: 'error',
            error: errorMessage,
          });

          setToast({
            variant: 'error',
            open: true,
            message: `Error reverting extraction ${i + 1}: ${errorMessage}`,
          });
        }
      }

      // Update the correction summary
      setBulkCorrectionSummary(correctionsSummary);

      const hasSuccessfulUpdates = correctionsSummary.some((status) => status.status === 'success');
      const hasErrors = correctionsSummary.some((status) => status.status === 'error');

      if (hasSuccessfulUpdates) {
        if (hasErrors) {
          setToast({
            variant: 'warning',
            open: true,
            message:
              'Some corrections were reverted successfully, but some failed. Check the status below.',
          });
        } else {
          setToast({
            variant: 'success',
            open: true,
            message: 'All corrections were reverted successfully',
          });
        }
      } else {
        setToast({
          variant: 'error',
          open: true,
          message: 'No corrections were reverted successfully',
        });
      }

      clearBulkCorrectionsRevertData();
    } catch (error) {
      setToast({
        variant: 'error',
        open: true,
        message: 'Failed to revert changes',
      });
    }
  };

  return (
    <>
      <IconButtonWhiteBg
        disabled={false}
        onClick={() => setOpen(true)}
        id="icon-revert-bulk-corrections"
        cypressId="btn-revert-bulk-corrections"
        tooltip="Undo Bulk Corrections"
      >
        <UndoIcon style={{ color: 'black', fontSize: '14px' }} />
      </IconButtonWhiteBg>
      <DialogGlynt
        open={open}
        closeModal={handleCloseConfirmation}
        cypressId="dialog-revert-bulk-corrections"
      >
        <DialogTitleGlynt
          title={'Confirmation'}
          closeModal={handleCloseConfirmation}
          startIcon="checkbox"
        />
        <DialogContentGlynt>
          <DialogContentText sx={{ padding: '10px 3px' }}>
            Are you sure you want to revert the last round of bulk corrections?
          </DialogContentText>
        </DialogContentGlynt>
        <DialogActionsGlynt
          handleSubmit={handleUndo}
          closeModal={handleCloseConfirmation}
          cancelCypressId="btn-revert-bulk-corrections-modal-cancel"
          submitCypressId="btn-revert-bulk-corrections-modal-submit"
          clearCypressId="btn-revert-bulk-corrections-modal-clear"
          clearButton={clearBulkCorrectionsRevertData}
          showClearButton={true}
        />
      </DialogGlynt>
    </>
  );
};

export default IconRevertBulkCorrections;
